import type {
  ReportDashboardWidgetInstance,
  ReportWidgetAccentColor,
  ReportDateRangeFilter,
  ReportFunnelStatusFunnelWidgetInstance,
  ReportFunnelStatusTimeseriesWidgetInstance,
  ReportWidgetCatalogItem,
  ReportWidgetKind,
  ReportUserAssignmentRankingWidgetInstance,
} from 'src/api/generated';
import type { TranslateParams } from 'src/modules/shared/types/i18n/i18n.type';
import type { TranslationKey } from 'src/modules/shared/types/i18n/translation-keys.type';
import {
  isFunnelStatusFunnelWidget,
  isFunnelStatusTimeseriesWidget,
  isUserAssignmentRankingWidget,
  REPORT_WIDGET_KIND,
  toReportWidgetKind,
} from '../utils/report-dashboard';
import { getReportDateRangeValidationMessage } from '../utils/report-date-range';

/**
 * Translation function used by report dashboard helpers.
 */
export type ReportDashboardTranslator = (
  key: TranslationKey,
  params?: TranslateParams
) => string;

/**
 * Report widget kind used by the dashboard UI.
 */
export type DashboardWidgetKind = ReportWidgetKind;
type WidgetKindInput =
  | ReportDashboardWidgetInstance['kind']
  | DashboardWidgetKind;

const FUNNEL_WIDGET_KINDS = new Set<DashboardWidgetKind>([
  REPORT_WIDGET_KIND.FUNNEL_STATUS_FUNNEL,
  REPORT_WIDGET_KIND.FUNNEL_STATUS_FIRST_ENTRY_TIMESERIES,
  REPORT_WIDGET_KIND.USER_ASSIGNMENT_RANKING,
]);

const USER_FILTER_WIDGET_KINDS = new Set<DashboardWidgetKind>([
  REPORT_WIDGET_KIND.FUNNEL_STATUS_FUNNEL,
  REPORT_WIDGET_KIND.FUNNEL_STATUS_FIRST_ENTRY_TIMESERIES,
  REPORT_WIDGET_KIND.USER_ASSIGNMENT_RANKING,
]);

/**
 * Returns whether a widget relies on funnel data.
 */
export const usesFunnel = (kind: WidgetKindInput): boolean =>
  FUNNEL_WIDGET_KINDS.has(toReportWidgetKind(kind));

/**
 * Returns whether a widget supports historical assignee filtering.
 */
export const supportsWidgetUserFilter = (kind: WidgetKindInput): boolean =>
  USER_FILTER_WIDGET_KINDS.has(toReportWidgetKind(kind));

/**
 * Normalizes a catalog widget kind to the dashboard widget kind union.
 */
export const toDashboardWidgetKind = (
  kind: ReportWidgetCatalogItem['kind']
): DashboardWidgetKind => toReportWidgetKind(kind);

/**
 * Resolves the fallback title for a widget kind.
 */
export const getWidgetTitleLabel = (
  kind: WidgetKindInput,
  widgetTitleByKind: Map<DashboardWidgetKind, string>,
  fallbackTitle: string
): string => widgetTitleByKind.get(toReportWidgetKind(kind)) ?? fallbackTitle;

const replaceWidgetFilters = <TWidget extends ReportDashboardWidgetInstance>(
  widget: TWidget,
  filters: TWidget['filters']
): TWidget =>
  ({
    ...widget,
    filters,
  }) as TWidget;

/**
 * Clones a widget draft without changing its discriminated shape.
 */
export const cloneWidget = <TWidget extends ReportDashboardWidgetInstance>(
  widget: TWidget
): TWidget =>
  replaceWidgetFilters(widget, {
    ...widget.filters,
  } as TWidget['filters']);

/**
 * Updates the user-defined title for a widget.
 */
export const updateWidgetTitle = (
  widget: ReportDashboardWidgetInstance,
  title: string
): ReportDashboardWidgetInstance => ({
  ...widget,
  title,
});

/**
 * Updates the optional badge label for a widget.
 */
export const updateWidgetLabel = (
  widget: ReportDashboardWidgetInstance,
  label: string
): ReportDashboardWidgetInstance => ({
  ...widget,
  label,
});

/**
 * Updates the optional accent color for a widget card.
 */
export const updateWidgetColor = (
  widget: ReportDashboardWidgetInstance,
  color: ReportWidgetAccentColor
): ReportDashboardWidgetInstance => ({
  ...widget,
  color,
});

/**
 * Updates the date range for any widget type.
 */
export const updateWidgetDateRange = <
  TWidget extends ReportDashboardWidgetInstance,
>(
  widget: TWidget,
  dateRange: ReportDateRangeFilter
): TWidget =>
  replaceWidgetFilters(widget, {
    ...widget.filters,
    dateRange,
  } as TWidget['filters']);

/**
 * Returns the selected funnel for the widget, if applicable.
 */
export const getWidgetFunnelId = (
  widget: ReportDashboardWidgetInstance | null
): number | undefined => {
  if (!widget || !('funnelId' in widget.filters)) {
    return undefined;
  }

  return widget.filters.funnelId ?? undefined;
};

/**
 * Replaces the selected funnel and clears dependent funnel filters.
 */
export const updateWidgetFunnel = (
  widget: ReportDashboardWidgetInstance,
  funnelId?: number
): ReportDashboardWidgetInstance => {
  if (isFunnelStatusFunnelWidget(widget)) {
    return {
      ...widget,
      filters: {
        funnelId,
        statusIds: [],
        userIds: widget.filters.userIds,
        breakdownFieldDefinitionId: null,
        dateRange: widget.filters.dateRange,
      },
    };
  }

  if (isFunnelStatusTimeseriesWidget(widget)) {
    return {
      ...widget,
      filters: {
        funnelId,
        statusId: null,
        userIds: widget.filters.userIds,
        breakdownFieldDefinitionId: null,
        dateRange: widget.filters.dateRange,
      },
    };
  }

  if (isUserAssignmentRankingWidget(widget)) {
    return {
      ...widget,
      filters: {
        funnelId,
        userIds: widget.filters.userIds,
        visibleStatusIds: [],
        dateRange: widget.filters.dateRange,
      },
    };
  }

  return widget;
};

/**
 * Updates visible statuses for the funnel distribution widget.
 */
export const updateWidgetStatusIds = (
  widget: ReportDashboardWidgetInstance,
  statusIds: number[]
): ReportDashboardWidgetInstance => {
  if (!isFunnelStatusFunnelWidget(widget)) {
    return widget;
  }

  return {
    ...widget,
    filters: {
      ...widget.filters,
      statusIds,
    },
  };
};

/**
 * Updates the selected status for the timeseries widget.
 */
export const updateWidgetStatusId = (
  widget: ReportDashboardWidgetInstance,
  statusId: number | null
): ReportDashboardWidgetInstance => {
  if (!isFunnelStatusTimeseriesWidget(widget)) {
    return widget;
  }

  return {
    ...widget,
    filters: {
      ...widget.filters,
      statusId,
    },
  };
};

/**
 * Updates visible table columns for the assignment widget.
 */
export const updateWidgetVisibleStatusIds = (
  widget: ReportDashboardWidgetInstance,
  visibleStatusIds: number[]
): ReportDashboardWidgetInstance => {
  if (!isUserAssignmentRankingWidget(widget)) {
    return widget;
  }

  return {
    ...widget,
    filters: {
      ...widget.filters,
      visibleStatusIds,
    },
  };
};

/**
 * Returns the selected users for widgets that support assignee filtering.
 */
export const getWidgetUserIds = (
  widget: ReportDashboardWidgetInstance
): number[] => ('userIds' in widget.filters ? widget.filters.userIds : []);

/**
 * Updates the selected assignee ids for supported widgets.
 */
export const updateWidgetUserIds = <
  TWidget extends ReportDashboardWidgetInstance,
>(
  widget: TWidget,
  userIds: number[]
): TWidget => {
  const normalizedUserIds = [...new Set(userIds)].sort(
    (left, right) => left - right
  );

  if (!('userIds' in widget.filters)) {
    return widget;
  }

  return replaceWidgetFilters(widget, {
    ...widget.filters,
    userIds: normalizedUserIds,
  } as TWidget['filters']);
};

/**
 * Updates the optional breakdown field for funnel-based widgets.
 */
export const updateWidgetBreakdownField = <
  TWidget extends ReportDashboardWidgetInstance,
>(
  widget: TWidget,
  breakdownFieldDefinitionId: number | null
): TWidget => {
  if (!('breakdownFieldDefinitionId' in widget.filters)) {
    return widget;
  }

  return replaceWidgetFilters(widget, {
    ...widget.filters,
    breakdownFieldDefinitionId,
  } as TWidget['filters']);
};

/**
 * Updates the row limit for the property ranking widget.
 */
export const updateWidgetLimit = <
  TWidget extends ReportDashboardWidgetInstance,
>(
  widget: TWidget,
  limit: number
): TWidget => {
  if (!('limit' in widget.filters)) {
    return widget;
  }

  return replaceWidgetFilters(widget, {
    ...widget.filters,
    limit,
  } as TWidget['filters']);
};

/**
 * Returns whether the widget has the minimum filters required to query data.
 */
export const hasRequiredWidgetFilters = (
  widget: ReportDashboardWidgetInstance
): boolean => {
  if (isFunnelStatusFunnelWidget(widget)) {
    return Boolean(
      widget.filters.funnelId && widget.filters.statusIds.length > 0
    );
  }

  if (isFunnelStatusTimeseriesWidget(widget)) {
    return Boolean(widget.filters.funnelId && widget.filters.statusId);
  }

  if (isUserAssignmentRankingWidget(widget)) {
    return Boolean(
      widget.filters.funnelId && widget.filters.visibleStatusIds.length > 0
    );
  }

  return true;
};

const createWidgetIssues = (dateRangeIssue: string | null): string[] =>
  dateRangeIssue ? [dateRangeIssue] : [];

const getFunnelStatusFunnelSetupIssues = (
  widget: ReportFunnelStatusFunnelWidgetInstance,
  t: ReportDashboardTranslator,
  dateRangeIssue: string | null
): string[] => {
  const issues = createWidgetIssues(dateRangeIssue);

  if (!widget.filters.funnelId) {
    issues.push(t('reports.dashboard.setupIssues.selectFunnel'));
  }

  if (widget.filters.statusIds.length === 0) {
    issues.push(t('reports.dashboard.setupIssues.selectAtLeastOneStatus'));
  }

  return issues;
};

const getFunnelStatusTimeseriesSetupIssues = (
  widget: ReportFunnelStatusTimeseriesWidgetInstance,
  t: ReportDashboardTranslator,
  dateRangeIssue: string | null
): string[] => {
  const issues = createWidgetIssues(dateRangeIssue);

  if (!widget.filters.funnelId) {
    issues.push(t('reports.dashboard.setupIssues.selectFunnel'));
  }

  if (!widget.filters.statusId) {
    issues.push(t('reports.dashboard.setupIssues.selectTimeseriesStatus'));
  }

  return issues;
};

const getUserAssignmentRankingSetupIssues = (
  widget: ReportUserAssignmentRankingWidgetInstance,
  t: ReportDashboardTranslator,
  dateRangeIssue: string | null
): string[] => {
  const issues = createWidgetIssues(dateRangeIssue);

  if (!widget.filters.funnelId) {
    issues.push(t('reports.dashboard.setupIssues.selectAssignmentFunnel'));
  }

  if (widget.filters.visibleStatusIds.length === 0) {
    issues.push(t('reports.dashboard.setupIssues.selectStatusColumn'));
  }

  return issues;
};

/**
 * Builds user-facing setup guidance for incomplete or invalid widgets.
 */
export const getWidgetSetupIssues = (
  widget: ReportDashboardWidgetInstance,
  t: ReportDashboardTranslator
): string[] => {
  if (widget.validation?.issues && widget.validation.issues.length > 0) {
    return widget.validation.issues;
  }

  const dateRangeIssue = getReportDateRangeValidationMessage(
    widget.filters.dateRange,
    t
  );

  if (isFunnelStatusFunnelWidget(widget)) {
    return getFunnelStatusFunnelSetupIssues(widget, t, dateRangeIssue);
  }

  if (isFunnelStatusTimeseriesWidget(widget)) {
    return getFunnelStatusTimeseriesSetupIssues(widget, t, dateRangeIssue);
  }

  if (isUserAssignmentRankingWidget(widget)) {
    return getUserAssignmentRankingSetupIssues(widget, t, dateRangeIssue);
  }

  return dateRangeIssue
    ? [dateRangeIssue]
    : [t('reports.dashboard.setupIssues.reviewConfiguration')];
};

/**
 * Shared chart color palette for widget previews and renderers.
 */
export const getSeriesColor = (index: number): string => {
  const palette = [
    '#09090A',
    '#2196F3',
    '#008080',
    '#10b981',
    '#f59e0b',
    '#FF007F',
  ];
  return palette[index % palette.length];
};
